import { Dispatch, SetStateAction } from 'react';
import { Flex, VStack, Box, FormControl } from '@cad-arc';
import { TransportForm } from '@mark43/cad-api';
import { FormCheckbox } from '../../forms/integrated-form-controls/form-checkbox';
import { FormInput } from '../../forms/integrated-form-controls/form-input';
import { FormLocationSelect } from '../../forms/integrated-form-controls/form-location-select';
import { FormNumberInput } from '../../forms/integrated-form-controls/form-number-input';
import { FormTextarea } from '../../forms/integrated-form-controls/form-text-area';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { HospitalTransportLocation } from './components/hospital-transport-location';

export function TransportDrawerForm({
    showHospitalTransportLocationSelect,
    isSubmitInProgress,
    showHospitalLocationRequiredErrorMessage,
    setShowHospitalLocationRequiredErrorMessage,
}: {
    showHospitalTransportLocationSelect: boolean;
    isSubmitInProgress: boolean;
    showHospitalLocationRequiredErrorMessage: boolean;
    setShowHospitalLocationRequiredErrorMessage: Dispatch<SetStateAction<boolean>>;
}): React.ReactElement {
    const { form } = useIntegratedForm<TransportForm>();
    const id = form.watch('id');
    const transportExists = !!id;
    const { subpremiseSupport } = useEnabledFeatures();
    return (
        <VStack spacing={4} align="left">
            <FormControl label="Odometer" isRequired>
                <FormNumberInput
                    autoFocus={true}
                    name="odometerStart"
                    rules={{
                        validate: (odometerStart) => {
                            if (transportExists) {
                                return true;
                            }
                            if (!odometerStart) {
                                return 'Required';
                            }
                            if (odometerStart < 0) {
                                return 'Cannot be less than 0';
                            }
                            if (odometerStart > 999999) {
                                return 'Cannot be greater than 999,999';
                            }
                        },
                    }}
                    control={form.control}
                    placeholder="Start"
                    isDisabled={transportExists}
                    size="md"
                />
                <FormNumberInput
                    name="odometerEnd"
                    rules={{
                        validate: (odometerEnd) => {
                            const odometerStart = form.getValues('odometerStart');
                            if (!transportExists) {
                                return true;
                            }
                            if (!odometerEnd) {
                                return 'Required';
                            }
                            if (!odometerStart) {
                                if (Number(odometerEnd) < 0) {
                                    return 'Cannot be less than 0';
                                }
                                return true;
                            }
                            if (Number(odometerEnd) < Number(odometerStart)) {
                                return 'Cannot be less than start';
                            }
                            if (Number(odometerEnd) > 1000000) {
                                return 'Cannot be greater than 1,000,000';
                            }
                        },
                    }}
                    control={form.control}
                    placeholder="End"
                    isDisabled={!transportExists}
                    size="md"
                />
            </FormControl>
            {showHospitalTransportLocationSelect ? (
                <HospitalTransportLocation
                    isSubmitInProgress={isSubmitInProgress}
                    showHospitalLocationRequiredErrorMessage={
                        showHospitalLocationRequiredErrorMessage
                    }
                    setShowHospitalLocationRequiredErrorMessage={
                        setShowHospitalLocationRequiredErrorMessage
                    }
                />
            ) : (
                <FormLocationSelect
                    label="Location To"
                    name="consolidatedLocationView"
                    rules={{ required: 'Required' }}
                    control={form.control}
                />
            )}
            {!subpremiseSupport && (
                <Flex direction="row" mt={2}>
                    <Box mr={2}>
                        <FormInput
                            name="subPremise"
                            control={form.control}
                            placeholder="Apt, Unit"
                        />
                    </Box>
                </Flex>
            )}
            <FormCheckbox
                name="updateUnitLocation"
                control={form.control}
                aria-label="Update Unit Location"
                label="Update Unit Location"
            />
            <FormTextarea
                name="comments"
                control={form.control}
                textareaProps={{
                    maxLength: 255,
                }}
            />
        </VStack>
    );
}
